import { Link, useNavigate } from "@remix-run/react";
import { json, redirect } from "@remix-run/node";
import { createSessionCookie, getSessionUser } from "~/utils/firebase.server";
import { prisma } from "~/utils/db.server";
import { normalizeEmail } from "~/utils/email.server";
import icon from "~/assets/icon.png";
import { useState } from "react";
import { signIn } from "~/utils/firebase.client";

export async function action({ request }: { request: Request }) {
  const formData = await request.formData();
  const idToken = formData.get("idToken") as string;
  const rawEmail = formData.get("email") as string;
  const email = normalizeEmail(rawEmail);
  const headers = new Headers();

  if (!email) {
    return json({ error: "Invalid email" }, { status: 400 });
  }

  try {
    await createSessionCookie(idToken, headers);

    console.log("Session cookie created");

    // Get or create user in our database (email stored lowercase for case-insensitive uniqueness)
    await prisma.user.upsert({
      where: { email },
      update: {},
      create: {
        email,
        name: rawEmail.split("@")[0],
      },
    });

    
    console.log("User created");

    return redirect("/", {
      headers,
    });
  } catch (error) {
    console.log("Error creating session cookie", error);
    const message = error instanceof Error ? error.message : "Authentication failed";
    return json({ error: message }, { status: 400 });
  }
}

export async function loader({ request }: { request: Request }) {
  const user = await getSessionUser(request);
  if (user) {
    return redirect("/");
  }
  return null;
}

export default function Login() {
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    const formData = new FormData(e.currentTarget);
    const email = formData.get("email") as string;
    const password = formData.get("password") as string;

    try {
      // Sign in with Firebase client SDK
      const idToken = await signIn(email, password);

      // Send idToken to server to create session cookie
      const response = await fetch("/login", {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: new URLSearchParams({
          idToken,
          email,
        }),
      });

      if (response.redirected) {
        navigate("/");
      } else {
        const data = await response.json();
        setError(data.error || "Login failed");
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : "Authentication failed";
      setError(message);
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen bg-bar-dark flex flex-col items-center justify-center px-4 py-12 relative overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_rgba(234,179,8,0.08)_0%,_transparent_50%)]" />
      <div className="absolute inset-0 bg-[linear-gradient(to_bottom_right,_rgba(255,255,255,0.02)_0%,_transparent_50%)]" />
      <div className="relative w-full max-w-md space-y-8 animate-slide-up">
        <div className="text-center">
          <div className="flex justify-center mb-6">
            <div className="h-20 w-20 rounded-2xl bg-bar-accent p-3 shadow-glow ring-2 ring-bar-accent/30">
              <img src={icon} alt="Hermansbar" className="h-full w-full object-contain" />
            </div>
          </div>
          <h1 className="text-3xl font-bold text-bar-cream tracking-tight">Welkom terug!</h1>
          <p className="mt-2 text-bar-muted text-sm">
            Nog geen account?{" "}
            <Link to="/signup" className="font-semibold text-bar-accent hover:text-bar-accent-hover transition-colors">
              Meld je aan
            </Link>
          </p>
        </div>
        <form onSubmit={handleSubmit} className="space-y-5">
          {error && (
            <div className="rounded-xl bg-red-500/20 border border-red-500/30 p-4 text-sm text-red-200">
              {error}
            </div>
          )}
          <div className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-bar-cream mb-1.5">
                Email adres
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                className="block w-full rounded-xl bg-bar-surface border border-white/10 px-4 py-3 text-bar-cream placeholder-bar-muted focus:border-bar-accent focus:ring-2 focus:ring-bar-accent/20 outline-none transition-all"
                placeholder="jou@voorbeeld.nl"
              />
            </div>
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-bar-cream mb-1.5">
                Wachtwoord
              </label>
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="current-password"
                required
                className="block w-full rounded-xl bg-bar-surface border border-white/10 px-4 py-3 text-bar-cream placeholder-bar-muted focus:border-bar-accent focus:ring-2 focus:ring-bar-accent/20 outline-none transition-all"
                placeholder="••••••••"
              />
            </div>
          </div>
          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full flex items-center justify-center gap-2 rounded-xl bg-bar-accent hover:bg-bar-accent-hover disabled:opacity-60 disabled:cursor-not-allowed text-bar-dark px-4 py-3.5 font-bold shadow-bar hover:shadow-glow transition-all duration-200"
          >
            {isSubmitting ? "Inloggen..." : "Inloggen"}
          </button>
        </form>
      </div>
    </div>
  );
}
